import PropTypes from 'prop-types';
import React, {Component} from 'react';
import _ from 'lodash';
import Dialog from 'material-ui/Dialog';
import FontIcon from 'material-ui/FontIcon';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import IconButton from 'material-ui/IconButton';
import Toggle from 'material-ui/Toggle';
import CircularProgress from 'material-ui/CircularProgress';
import * as Colors from 'material-ui/styles/colors';
import InlineButton from 'shared/components/InlineButton';
import {eventOnFirstTime} from 'shared/helpers/analyticsHelper';
import InlineNotification, {
  LEVEL_INFO,
  LEVEL_NOTICE,
  LEVEL_ERROR,
  getColorStyle,
} from 'crazyEditor/components/EditorUI/BaseUI/InlineMessages/InlineNotification';
import ImageUploader from 'MyAccount/ViewDesigns/components/Wizard/ImageUploader';
import GlobalStore from './GlobalStore';
import {EDIT_PAID_FEATURES_DIALOG_NAME, BACKGROUND_REMOVAL} from 'MyAccount/AccountPage/PlanAndBilling/Consts';
import {
  uploadFile,
  getImage,
  getS3VersionedImage,
  getS3VersionedUrl,
  isClearedSrc,
  cleaeredToOriginalSrc,
  originalToCleaeredSrc,
  getClearedBackgroundImage,
} from 'MyAccount/ViewDesigns/helpers/uploadHelper';
import {isCrazylisterHostedImage} from 'MyAccount/ViewDesigns/helpers/resizeImageUrlHelper';
import SelectableBox from '../../EditorUI/BaseUI/SelectableBoxes/SelectableBox';
import * as Consts from './Consts.js';
import styles from './ImageDialogStyles';
import Camera51TouchUp from './Camera51TouchUp';
import ImageProperties from './components/ImageProperties';
import ClearedBackgroundImage from './components/ClearedBackgroundImage';
import ImageManager from './ImageManagerConnect';
import ButtonSection from './components/ButtonSection';
import {WebAddressesLoader} from 'MyAccount/AssetManager/ImageManager/components/WebAddressesLoader';
import {checkFileName} from 'MyAccount/AssetManager/ImageManager/helpers';
import connectWithRouter from 'shared/components/redux/connectWithRouter';

const PREVIEW_IMAGE_SIZE = 220;

const TRANSITOIN_DURATION_MS = 250;

const SELECTED_VARIATION = {
  ORIGINAL: 0,
  CLEARED: 1,
};

const IMAGE_STATUS = {
  NONE: 0,
  LOADING: 1,
  DONE: 2,
};

const ERROR_LEVEL = {
  INFO: LEVEL_INFO,
  NOTICE: LEVEL_NOTICE,
  ERROR: LEVEL_ERROR,
};

// camera 51 status
const PROCESSING_STATUS_SUCCESS = 0;
const PROCESSING_STATUS_LOW_CONTRAST = 2;
const PROCESSING_STATUS_CLUTTERED_BACKGROUND = 4;
const PROCESSING_STATUS_ALREADY_CLEARED = 5;
// for internal use
const PROCESSING_STATUS_TIMEOUT = 6;

// internal status
const PROCESSING_STATUS_NOT_STARTED = 1000;
const PROCESSING_STATUS_PROCESSING = 1001;
const PROCESSING_STATUS_NOT_SUPPORTED = 1002;

const INITIAL_STATE = {
  selectedVariation: SELECTED_VARIATION.ORIGINAL,
  clearedImage: null,
  originalImageStatus: IMAGE_STATUS.NONE,
  clearedImageStatus: IMAGE_STATUS.NONE,
  clearedImageProcessingStatus: PROCESSING_STATUS_NOT_STARTED,
  uploadedImage: null,
  camera51TouchUpOpen: false,
  imageProperties: null,
  showWebAddressesLoader: false,
  imageManagerState: {
    currentFolder: '',
    photos: [],
    uploadImages: [],
  },
  webUrlsValid: false,
  toggled: false,
};

@connectWithRouter(state => ({
  query: state.routing.location.query,
  pathname: state.routing.location.pathname,
  mayUseCamera51: state.user.isOnTrial || (state.user.paymentActive && state.user.backgroundRemoval),
}))
export default class ImageDialog extends Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    imageUtils: PropTypes.object.isRequired,
  };

  constructor(props) {
    const {newElement} = props;
    super(props);
    const noticeColors = getColorStyle(ERROR_LEVEL.NOTICE);
    this.PROCESSING_STATUS_MESSAGE = {};
    this.UNKNOWN_ERROR_MESSAGE = {
      message: 'Unknown error occurred while trying to remove background from this image.',
      level: ERROR_LEVEL.NOTICE,
    };
    this.PROCESSING_STATUS_MESSAGE[PROCESSING_STATUS_SUCCESS] = {level: {}};
    this.PROCESSING_STATUS_MESSAGE[PROCESSING_STATUS_LOW_CONTRAST] = {
      message: "Due to low contrast we couldn't remove the background automatically.",
      level: ERROR_LEVEL.NOTICE,
      touchUp: (
        <span>
          You may{' '}
          <InlineButton
            onClick={() => this.onTouchUpClicked()}
            style={{...noticeColors, fontSize: 16}}
            label="remove it manually"
          />
        </span>
      ),
    };
    this.PROCESSING_STATUS_MESSAGE[PROCESSING_STATUS_CLUTTERED_BACKGROUND] = {
      message: "Due to a cluttered background we couldn't remove the background automatically.",
      level: ERROR_LEVEL.NOTICE,
      touchUp: (
        <span>
          You may{' '}
          <InlineButton
            onClick={() => this.onTouchUpClicked()}
            style={{...noticeColors, fontSize: 16}}
            label="remove it manually"
          />
        </span>
      ),
    };
    this.PROCESSING_STATUS_MESSAGE[PROCESSING_STATUS_ALREADY_CLEARED] = {
      message: 'Your image already has a white background.',
      level: ERROR_LEVEL.NOTICE,
      touchUp: (
        <span>
          You may{' '}
          <InlineButton
            onClick={() => this.onTouchUpClicked()}
            style={{...noticeColors, fontSize: 16}}
            label="edit it manually"
          />{' '}
          if you wish.
        </span>
      ),
    };
    this.PROCESSING_STATUS_MESSAGE[PROCESSING_STATUS_NOT_STARTED] = {
      message:
        'Once your image is uploaded we will try to automatically remove its background. This may take a few minutes.',
      level: ERROR_LEVEL.NOTICE,
    };
    this.PROCESSING_STATUS_MESSAGE[PROCESSING_STATUS_PROCESSING] = {
      message: null,
      level: ERROR_LEVEL.NOTICE,
    };
    this.PROCESSING_STATUS_MESSAGE[PROCESSING_STATUS_NOT_SUPPORTED] = {
      message: 'Cleared background is not available for this image',
      level: ERROR_LEVEL.NOTICE,
    };
    this.PROCESSING_STATUS_MESSAGE[PROCESSING_STATUS_TIMEOUT] = {
      message: "Something went wrong while we tried to remove image's background",
      level: ERROR_LEVEL.NOTICE,
      touchUp: (
        <span>
          You may either continue without removing background or{' '}
          <InlineButton
            onClick={() => {
              window.Intercom(
                'showNewMessage',
                'Hello Crazylister\n' +
                  "Something went wrong when I tried to clear an image's background.\n" +
                  '\nWhat should I do?'
              );
            }}
          >
            contact support
          </InlineButton>
          .
        </span>
      ),
    };

    this.onAddUpdate = this.onAddUpdate.bind(this);
    this.getElementProps = this.getElementProps.bind(this);

    this.state = {INITIAL_STATE};
  }

  componentWillReceiveProps(nextProps) {
    const {open, mayUseCamera51} = nextProps;
    if (!this.props.open && open) {
      this.onOpen(nextProps);
    } else if (this.props.open && !open) {
      this.onClose();
    }
    if (!this.props.mayUseCamera51 && mayUseCamera51) {
      this.props.onAddUpdate(this.getElementProps());
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!_.get(prevState, 'uploadedImage.ready') && _.get(this.state, 'uploadedImage.ready') && this.state.toggled) {
      this.handleToggle(undefined, true);
    }
  }

  onOpen(nextProps) {
    eventOnFirstTime('New Image Upload Dialog - First Time');
    const {selectedElement, newElement} = nextProps;
    let newState = {};
    if (!newElement) {
      const {data, layout} = selectedElement;
      const src = data.url;
      newState.imageProperties = _.assign(
        {newElement},
        _.pick(data, ['title', 'href']),
        _.pick(layout, ['height', 'width', 'aspectRatio'])
      );
      if (!_.isEmpty(src)) {
        if (isCrazylisterHostedImage(src)) {
          newState = {...newState, ...this.loadImages(src)};
        } else {
          newState.imageProperties.src = src;
          newState = {...newState};
        }
      }
    }
    this.setState({...INITIAL_STATE, ...newState});
  }

  onClose = () => {
    if (this._lastUploadXhr) {
      this._lastUploadXhr.abort();
      this._lastUploadXhr = null;
    }
  };

  onAddUpdate() {
    const {selectedVariation} = this.state;
    const {mayUseCamera51, pathname, query} = this.props;
    if (selectedVariation === SELECTED_VARIATION.CLEARED) {
      window.analytics.track('Cleared Image Selected');
      if (!mayUseCamera51) {
        window.analytics.track('Offered Camera 51');
        this.context.router.push({
          pathname,
          query: {
            ...query,
            openDialog: EDIT_PAID_FEATURES_DIALOG_NAME,
            paidFeatureName: BACKGROUND_REMOVAL,
          },
        });
        return;
      }
    }
    this.props.onAddUpdate(this.getElementProps());
  }

  onTouchUpClicked = () => {
    this.setState({camera51TouchUpOpen: true});
    window.analytics.track('C51 TouchUp Clicked');
  };

  onWebUrlsValidChange = webUrlsValid => {
    this.setState({webUrlsValid});
  };

  onCancel = () => {
    const {onCancel} = this.props;
    const {showWebAddressesLoader} = this.state;
    if (showWebAddressesLoader) {
      this.closeWebAddressesLoader();
    } else {
      onCancel();
    }
  };

  onContinue = orinUrl => {
    const {showWebAddressesLoader} = this.state;
    if (showWebAddressesLoader) {
      this.uploadImageFromUrl();
    } else {
      getS3VersionedUrl(orinUrl, url => {
        this.setState({...this.loadImages(url)});
      });
    }
  };

  getSelectedImageAndProperties() {
    const {selectedVariation, uploadedImage, clearedImage} = this.state;
    let selectedImageProperties;
    let selectedImage;

    selectedImageProperties = this.refs.imageProperties;
    if (selectedVariation === SELECTED_VARIATION.ORIGINAL) {
      selectedImage = uploadedImage && uploadedImage.img;
    } else {
      selectedImage = clearedImage;
    }

    return {selectedImage, selectedImageProperties};
  }

  getSelectedVariationFromToggle = () => {
    const {toggled} = this.state;
    return toggled ? SELECTED_VARIATION.CLEARED : SELECTED_VARIATION.ORIGINAL;
  };

  getElementProps() {
    const {selectedVariation, uploadedImage} = this.state;
    const imageAndProperties = this.getSelectedImageAndProperties();
    const imageProperties = imageAndProperties.selectedImageProperties;
    const selectedImage = imageAndProperties.selectedImage;
    if (uploadedImage && uploadedImage.c51TrackId) {
      if (selectedVariation === SELECTED_VARIATION.ORIGINAL) {
        _.defer(() => {
          window.camera51UserFunctions.sendEventTrackId(uploadedImage.c51TrackId, 201, 'accept_orig_image');
        });
      } else {
        _.defer(() => {
          window.camera51UserFunctions.sendEventTrackId(uploadedImage.c51TrackId, 201, 'accept_new_image');
        });
      }
    }
    return {
      layout: {
        width: imageProperties.getWidth(),
        height: imageProperties.getHeight(),
        aspectRatio: imageProperties.getAspectRatio(),
      },
      data: {
        url: selectedImage.src,
        title: imageProperties.getTitle(),
        href: imageProperties.getLink(),
      },
    };
  }

  setUploadedImage = imageUpdate => {
    const {uploadedImage} = this.state;
    const {newElement} = this.props;
    const stateUpdate = {};
    if (imageUpdate) {
      if (imageUpdate.inProgress === false) {
        this._lastUploadXhr = null;
        if (this._finder !== null) {
          this._finder.request('folder:refreshFiles');
        }
      }
      stateUpdate.originalImageStatus = IMAGE_STATUS.DONE;
      const img = _.get(imageUpdate, 'img');
      if (newElement && img && img.height) {
        const {width, height} = img;
        stateUpdate.imageProperties = {...this.state.imageProperties, width, height, aspectRatio: width / height};
      }
    } else {
      stateUpdate.originalImageStatus = IMAGE_STATUS.NONE;
    }
    this.setState({
      ...stateUpdate,
      uploadedImage: uploadedImage && imageUpdate ? {...uploadedImage, ...imageUpdate} : imageUpdate,
    });
  };

  getWebUrls = webUrls => {
    this.setState({webUrls});
  };

  closeWebAddressesLoader = () => {
    this.setState({showWebAddressesLoader: false});
  };

  uploadImagesDirectToEditor(files) {
    this.uploadFile(files[0]);
  }

  uploadImagesDirectToEditorFromUrl(src) {
    this.loadImages(src);
  }

  uploadImageFromUrl = () => {
    const {webUrls} = this.state;
    const urlsWithName = _.map(webUrls, url => ({...url, name: _.last(_.split(url.src, '/'))}));
    const {photos, uploadFiles, currentFolder} = this.state.imageManagerState;
    this.closeWebAddressesLoader();
    const checkNameArr = [];
    const path = currentFolder ? `/${_.replace(currentFolder, /\\/gi, '/')}/` : '/';
    if (_.size(webUrls) === 1) {
      this.uploadImagesDirectToEditorFromUrl(webUrls[0].src);
    }
    _.map(urlsWithName, (url, index) => {
      const newUrl = url.src;
      let name = url.name;
      if (!_.endsWith(name, '.jpg') || !_.endsWith(name, '.png') || !_.endsWith(name, '.jpeg')) {
        name = name
          .split('/')
          .pop()
          .split('#')[0]
          .split('?')[0];
      }
      const fileNameSplited = _.split(name, '.');
      let i = 0;
      while (
        name !== checkFileName(checkNameArr, name) ||
        name !== checkFileName(photos, name) ||
        name !== checkFileName(uploadFiles, name)
      ) {
        if (_.size(fileNameSplited) === 1) {
          name = `${fileNameSplited[0]}(${i})`;
        } else {
          const fileExt = _.last(fileNameSplited);
          name = `${_.join(_.dropRight(fileNameSplited), '.')}(${i}).${fileExt}`;
        }
        i += 1;
      }
      checkNameArr.push({name});
      this.context.imageUtils.doUploadFilesFromUrl({newUrl, path, name}, result => {
        if (_.get(result, 'success')) {
          this.closeWebAddressesLoader();
        } else {
          console.log('Error while editing image', _.get(result, 'error'));
        }
      });
    });
  };

  backArrowClicked = () => {
    const {camera51TouchUpOpen, uploadedImage} = this.state;
    if (camera51TouchUpOpen) {
      this.setState({camera51TouchUpOpen: false});
    } else if (uploadedImage) {
      this.setState(INITIAL_STATE);
    }
  };

  uploadFile = file => {
    this._lastUploadXhr = uploadFile(file, progress => {
      this.setUploadedImage({progress: progress});
    });
    this._lastUploadXhr
      .then((data, status, jqXHR) => {
        if (!data.uploaded) {
          this.setUploadedImage({failed: true, inProgress: false});
        } else {
          const amzVersionId = jqXHR.getResponseHeader('x-amz-version-id');
          const getImageFunction = amzVersionId ? getImage : getS3VersionedImage; // if we already have the version id no need tosend additional request
          const src = data.currentFolder.url + encodeURIComponent(data.fileName) + '?versionId=' + amzVersionId;
          const c51TrackId = jqXHR.getResponseHeader('x-c51TrackId');
          getImageFunction(src, img => {
            this.setUploadedImage({
              progress: 1,
              ready: true,
              src: img.src,
              storedName: data.fileName,
              inProgress: false,
              img: img,
              c51TrackId: c51TrackId,
            });
          });
        }
      })
      .fail(() => {
        this.setUploadedImage({failed: true, inProgress: false});
      });
    const preview = file.preview || window.URL.createObjectURL(file);
    file.preview = preview;
    this.setUploadedImage({
      ...file,
      originalFile: file,
      progress: 0,
      ready: false,
      failed: false,
      inProgress: true,
      deleted: false,
      preview: preview,
    });
  };

  handleToggle = (event, checked) => {
    const {
      clearedImageProcessingStatus,
      toggled,
      imageManagerState: {currentFolder},
      uploadedImage: {storedName, ready},
    } = this.state;
    if (checked && clearedImageProcessingStatus === PROCESSING_STATUS_NOT_STARTED && ready) {
      this.setState({toggled: true, clearedImageProcessingStatus: PROCESSING_STATUS_PROCESSING});
      getClearedBackgroundImage(currentFolder, storedName).then(response => {
        const {resultImageURL, processingResultCode, trackId} = response;
        const statusUpdate = {};
        statusUpdate.clearedImageProcessingStatus = processingResultCode;
        if (processingResultCode === PROCESSING_STATUS_SUCCESS) {
          statusUpdate.clearedImageStatus = IMAGE_STATUS.LOADING;
          getImage(resultImageURL, img => {
            this.setState({
              selectedVariation: img !== null && this.getSelectedVariationFromToggle(),
              clearedImage: img,
              clearedImageStatus: IMAGE_STATUS.DONE,
              trackId,
            });
          });
        } else {
          statusUpdate.clearedImageStatus = IMAGE_STATUS.DONE;
        }
      });
    } else {
      this.setState({
        selectedVariation: toggled || !ready ? SELECTED_VARIATION.ORIGINAL : SELECTED_VARIATION.CLEARED,
        toggled: !toggled,
      });
    }
  };

  isSelectedImageValid() {
    const selectedImage = this.getSelectedImageAndProperties().selectedImage;
    return selectedImage && selectedImage.height > 0;
  }

  loadImages = src => {
    let clearedImageSrc;
    let originalImageSrc;
    const statusUpdate = {};
    if (isClearedSrc(src)) {
      clearedImageSrc = src;
      originalImageSrc = cleaeredToOriginalSrc(src);
      statusUpdate.selectedVariation = SELECTED_VARIATION.CLEARED;
      statusUpdate.toggled = true;
    } else {
      originalImageSrc = src;
      clearedImageSrc = originalToCleaeredSrc(src);
      statusUpdate.selectedVariation = SELECTED_VARIATION.ORIGINAL;
      statusUpdate.toggled = false;
    }
    statusUpdate.originalImageStatus = IMAGE_STATUS.LOADING;
    getImage(originalImageSrc, originalImage => {
      this.setUploadedImage({
        progress: 1,
        ready: true,
        src: originalImage.src,
        preview: originalImage.src,
        inProgress: false,
        img: originalImage,
        imageProperties: {...this.state.imageProperties},
      });
    });
    statusUpdate.clearedImageStatus = IMAGE_STATUS.LOADING;
    getImage(clearedImageSrc, clearedImage => {
      this.setState({
        clearedImage,
        clearedImageStatus: IMAGE_STATUS.DONE,
        selectedVariation: this.getSelectedVariationFromToggle(),
      });
      if (clearedImage === null) {
        this.setState({
          clearedImageProcessingStatus: PROCESSING_STATUS_NOT_SUPPORTED,
          toggled: false,
          selectedVariation: SELECTED_VARIATION.ORIGINAL,
        });
      }
    });
    return statusUpdate;
  };

  shouldShowPreviewAndProps(state) {
    const {clearedImageStatus, originalImageStatus} = state;
    return clearedImageStatus !== IMAGE_STATUS.NONE || originalImageStatus !== IMAGE_STATUS.NONE;
  }

  reloadClearedBackgroundImage = callback => {
    this.setState({
      clearedImageStatus: IMAGE_STATUS.LOADING,
      clearedImageProcessingStatus: PROCESSING_STATUS_PROCESSING,
    });
    const {clearedImage} = this.state;
    getS3VersionedImage(clearedImage.src, img => {
      if (!_.isNil(img)) {
        this.setState({
          clearedImage: img,
          clearedImageStatus: IMAGE_STATUS.DONE,
          clearedImageProcessingStatus: PROCESSING_STATUS_SUCCESS,
          selectedVariation: this.getSelectedVariationFromToggle(),
        });
      }
      if (_.isFunction(callback)) {
        callback(!_.isNil(img));
      }
    });
  };

  _finder = null;
  _lastUploadXhr = null;

  render() {
    const {open, newElement} = this.props;
    const {
      selectedVariation,
      clearedImage,
      uploadedImage,
      imageProperties,
      selectedTab,
      clearedImageStatus,
      originalImageStatus,
      clearedImageProcessingStatus,
      camera51TouchUpOpen,
      toggled,
      showWebAddressesLoader,
      webUrlsValid,
      trackId,
    } = this.state;
    const {UNKNOWN_ERROR_MESSAGE, PROCESSING_STATUS_MESSAGE} = this;
    const processingStatusMessage = _.get(PROCESSING_STATUS_MESSAGE, clearedImageProcessingStatus, {
      ...UNKNOWN_ERROR_MESSAGE,
      message: UNKNOWN_ERROR_MESSAGE.message + ' error code: ' + clearedImageProcessingStatus,
    });
    const {message, touchUp, level} = processingStatusMessage;
    let previewAndProps;
    // while upload is in progress prevent user from switching tabs.
    const uploadInProgress = uploadedImage && uploadedImage.inProgress;
    const tabsProps = _.range(3).map(() => {
      return uploadInProgress ? {disabled: true, style: {opacity: '0.5'}} : {};
    });
    const dialogTitle = newElement ? 'Add Image' : 'Edit Image';
    tabsProps[selectedTab] = {};
    const PROCESSING_STATUS_ERROR =
      clearedImageProcessingStatus === PROCESSING_STATUS_CLUTTERED_BACKGROUND ||
      clearedImageProcessingStatus === PROCESSING_STATUS_LOW_CONTRAST ||
      clearedImageProcessingStatus === PROCESSING_STATUS_NOT_SUPPORTED ||
      clearedImageProcessingStatus === PROCESSING_STATUS_ALREADY_CLEARED ||
      clearedImageProcessingStatus === PROCESSING_STATUS_TIMEOUT ||
      clearedImageProcessingStatus === 101 ||
      clearedImageProcessingStatus === 103;
    const TOGGLED_DONE_NO_ERROR = toggled && clearedImageStatus === IMAGE_STATUS.DONE && !PROCESSING_STATUS_ERROR;
    const TOGGLE_NOT_DONE_NO_ERROR = toggled && clearedImageStatus !== IMAGE_STATUS.DONE && !PROCESSING_STATUS_ERROR;
    const translateX = TOGGLED_DONE_NO_ERROR ? 'translate(0px)' : 'translate(-280px)';
    const shouldShow = this.shouldShowPreviewAndProps(this.state);
    if (shouldShow) {
      const img = uploadedImage && uploadedImage.img;
      previewAndProps = (
        <div style={styles.previewAndProps}>
          <div style={styles.imagesPreviewContainer}>
            <div
              style={{
                position: 'absolute',
                top: 13,
                left: 20,
                transition: 'all ' + TRANSITOIN_DURATION_MS + 'ms ease-in-out',
                opacity: TOGGLED_DONE_NO_ERROR ? 1 : 0,
              }}
            >
              Select preferred image:
            </div>
            <SelectableBox
              key={'selectableBox' + SELECTED_VARIATION.ORIGINAL}
              style={{height: PREVIEW_IMAGE_SIZE, width: PREVIEW_IMAGE_SIZE, minWidth: PREVIEW_IMAGE_SIZE, zIndex: 2}}
              selected={selectedVariation === SELECTED_VARIATION.ORIGINAL}
              onClick={() => this.setState({selectedVariation: SELECTED_VARIATION.ORIGINAL})}
              variation={1}
              zDepth={1}
            >
              <ImageUploader
                image={uploadedImage}
                imageSize={PREVIEW_IMAGE_SIZE}
                deleteIconClass="clear"
                previewEnabled={false}
                onClickRemove={() => {
                  this.setState(INITIAL_STATE);
                }}
                onRetry={() => this.uploadFile(uploadedImage.originalFile)}
              />
            </SelectableBox>
            <div
              style={{
                margin: 20,
                transition: 'all ' + TRANSITOIN_DURATION_MS + 'ms ease-in-out',
                opacity: TOGGLED_DONE_NO_ERROR ? 1 : 0,
              }}
            >
              or
            </div>
            <SelectableBox
              key={'selectableBox' + SELECTED_VARIATION.CLEARED}
              style={{
                height: PREVIEW_IMAGE_SIZE,
                width: PREVIEW_IMAGE_SIZE,
                minWidth: PREVIEW_IMAGE_SIZE,
                transition: 'all ' + TRANSITOIN_DURATION_MS + 'ms ease-in-out',
                transform: translateX,
                zIndex: 1,
                opacity: TOGGLED_DONE_NO_ERROR ? 1 : 0,
              }}
              selected={selectedVariation === SELECTED_VARIATION.CLEARED}
              onClick={() => this.setState({selectedVariation: SELECTED_VARIATION.CLEARED})}
              selectable={clearedImage !== null}
              variation={2}
              zDepth={1}
            >
              <ClearedBackgroundImage
                imageSize={PREVIEW_IMAGE_SIZE}
                processing={img && !(clearedImage !== null)}
                img={clearedImage}
                processingStatus={clearedImageProcessingStatus}
              />
            </SelectableBox>
            <div
              style={{
                ...styles.camera51Actions,
                transform: translateX,
                transition: 'all ' + TRANSITOIN_DURATION_MS + 'ms ease-in-out',
              }}
            >
              <div
                style={{
                  transform: 'translate(-8px)',
                  display: TOGGLE_NOT_DONE_NO_ERROR ? 'flex' : 'none',
                  alignItems: 'center',
                  opacity: TOGGLE_NOT_DONE_NO_ERROR ? 1 : 0,
                  transition: 'all ' + TRANSITOIN_DURATION_MS + 'ms ease-in-out',
                }}
              >
                <CircularProgress color={Colors.lightGreen700} size={24} thickness={1.4} style={{margin: 13}} />
                <div color={Colors.grey700}>
                  {clearedImageProcessingStatus === PROCESSING_STATUS_NOT_STARTED
                    ? 'Uploading Image...'
                    : 'Clearing background...'}
                </div>
              </div>
              <div>
                <InlineButton
                  onClick={() => {
                    this.onTouchUpClicked();
                  }}
                  disabled={!TOGGLED_DONE_NO_ERROR}
                  style={{
                    transition: 'all ' + TRANSITOIN_DURATION_MS + 'ms ease-in-out',
                    opacity: TOGGLED_DONE_NO_ERROR ? 1 : 0,
                  }}
                >
                  Manually refine background removal
                </InlineButton>
              </div>
              {toggled && message && clearedImageProcessingStatus !== PROCESSING_STATUS_NOT_STARTED && (
                <InlineNotification
                  level={level}
                  style={{
                    width: '400px',
                  }}
                  animate={false}
                  onRequestClose={() => this.setState({toggled: false})}
                >
                  {message}
                  <br />
                  {touchUp}
                </InlineNotification>
              )}
            </div>
          </div>
          <ImageProperties img={img} initialImageProperties={imageProperties} ref="imageProperties" />
        </div>
      );
    }
    const buttonsSection = (
      <div style={styles.buttonsSection}>
        <FlatButton style={{marginRight: 20}} primary label="cancel" onClick={this.onCancel} />
        <RaisedButton
          disabled={!this.isSelectedImageValid()}
          style={styles.mainButton}
          primary
          onClick={this.onAddUpdate}
          label={newElement ? 'add to page' : 'update'}
        />
      </div>
    );
    return (
      <Dialog
        open={open}
        bodyStyle={{padding: 0}}
        contentStyle={{
          maxWidth: Consts.DIALOG_MAX_WIDTH,
          width: Consts.DIALOG_MAX_WIDTH,
          padding: 0,
          position: 'relative',
          height: Consts.MUI_DIALOG_MAX_HEIGHT,
          userSelect: 'none',
        }}
      >
        <GlobalStore>
          <div style={styles.dialogHeader}>
            <div style={styles.dialogTitle}>
              {
                <a
                  key={'1'}
                  style={{...styles.link, display: this.isSelectedImageValid() ? 'inline' : 'none'}}
                  onClick={this.backArrowClicked}
                >
                  <i style={styles.arrow} className="material-icons">
                    arrow_back
                  </i>
                </a>
              }
              {camera51TouchUpOpen ? 'Manual Background Remove' : dialogTitle}
            </div>
            <IconButton
              key="cancelButton"
              onClick={this.onCancel}
              iconStyle={{color: 'white'}}
              style={styles.closeButton}
            >
              <FontIcon className="material-icons">close</FontIcon>
            </IconButton>
          </div>
          {camera51TouchUpOpen ? (
            <Camera51TouchUp
              c51TrackId={trackId}
              onComplete={newUrl => {
                this.setState({camera51TouchUpOpen: false});
                getImage(newUrl, img => {
                  this.setState({
                    selectedVariation: img !== null && this.getSelectedVariationFromToggle(),
                    clearedImage: img,
                    clearedImageStatus: IMAGE_STATUS.DONE,
                  });
                });
              }}
              onCancel={() => {
                this.setState({camera51TouchUpOpen: false});
              }}
            />
          ) : (
            <div>
              {!shouldShow && (
                <div>
                  {showWebAddressesLoader ? (
                    <div style={{height: Consts.CONTENT_HEIGHT, overflowY: 'auto'}}>
                      <WebAddressesLoader
                        ref="webAddressesLoader"
                        onValidChange={this.onWebUrlsValidChange}
                        getUrls={this.getWebUrls}
                      />
                    </div>
                  ) : (
                    <ImageManager
                      resourceType="Images"
                      style={{height: Consts.CONTENT_HEIGHT}}
                      uploadImagesDirectToEditor={images => this.uploadImagesDirectToEditor(images)}
                      openIntegratedUploadFromUrlDialog={() => this.setState({showWebAddressesLoader: true})}
                      getImageManagerState={imageManagerState => this.setState({imageManagerState})}
                    />
                  )}
                  <ButtonSection
                    styles={styles}
                    description={!showWebAddressesLoader}
                    onContinue={this.onContinue}
                    inputValid={showWebAddressesLoader ? webUrlsValid : null}
                  />
                </div>
              )}
              <div
                style={{
                  width: '100%',
                  backgroundColor: Colors.grey800,
                  position:
                    _.isNull(uploadedImage) && originalImageStatus === IMAGE_STATUS.NONE ? 'absolute' : 'relative',
                  zIndex: _.isNull(uploadedImage) && originalImageStatus === IMAGE_STATUS.NONE ? -1 : 1,
                  maxHeight: Consts.MUI_DIALOG_MAX_HEIGHT_MINUS(Consts.TITLE_PADDING),
                }}
              >
                {previewAndProps}
                {shouldShow && buttonsSection}
              </div>
            </div>
          )}
        </GlobalStore>
      </Dialog>
    );
  }
}
